import { FormEvent, useRef, useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { CheckoutAccountStep } from "@/components/CheckoutAccountStep"
import { CheckoutLayout } from "@/components/CheckoutLayout"
import { CheckoutProgress } from "@/components/CheckoutProgress"
import { Divider, Row } from "@/components/PurchaseDetailsCard"
import { StripeProvider } from "@/components/StripeProvider"
import { Button } from "@/components/ui/button"
import { fetchJson, formatCents } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

type LifetimeProps = PageProps<{
  deal: { name: string; slug: string; price_cents: number; free: boolean }
  stripe_publishable_key: string
  payment_method_types: string[]
  logged_in_email: string | null
}>

export default function Lifetime() {
  const { props } = usePage<LifetimeProps>()
  const { deal } = props
  const authenticated = !!props.logged_in_email

  const [mode, setMode] = useState<"register" | "login">("register")

  const description = `${deal.name}: a lifetime Flywrite license — one seat, updates forever.`
  const stepTwoLabel = deal.free ? "Claim license" : "Payment details"

  return (
    <>
      <Head title={`${deal.name} — Flywrite Lifetime`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${deal.name} — Flywrite Lifetime`} />
        <meta property="og:description" content={description} />
      </Head>
      <CheckoutLayout
        mainHeader={
          <CheckoutProgress
            step={authenticated ? 2 : 1}
            authenticated={authenticated || mode === "login"}
            stepTwoLabel={stepTwoLabel}
          />
        }
        main={
          !authenticated ? (
            <CheckoutAccountStep
              mode={mode}
              onModeChange={setMode}
              registerIntro="Create your Flywrite account first — it's where your license key and devices live."
              loginIntro="Log in to your Flywrite account to continue."
              extraParams={{ ldt: deal.slug }}
            />
          ) : deal.free ? (
            <FreeClaimStep email={props.logged_in_email!} slug={deal.slug} />
          ) : (
            <StripeProvider
              publishableKey={props.stripe_publishable_key}
              deferred={{
                mode: "payment",
                amount: deal.price_cents,
                currency: "usd",
                paymentMethodTypes: props.payment_method_types,
              }}
            >
              <LifetimePaymentStep
                email={props.logged_in_email!}
                slug={deal.slug}
                priceCents={deal.price_cents}
              />
            </StripeProvider>
          )
        }
        sidebarHeader={<span className="text-sm font-medium text-ink-muted">Purchase Details</span>}
        sidebar={
          <div className="space-y-4">
            <dl className="space-y-4">
              <Row label="Product">
                <span className="font-medium text-ink-display">Flywrite Lifetime</span>
              </Row>
              <Divider />
              <Row label="Offer">{deal.name}</Row>
              <p className="text-xs text-ink-muted">
                One license key for one seat. All future updates included.
              </p>
              <Divider />
              <Row label="Total">
                <span className="font-semibold text-ink-display">
                  {deal.free ? "Free" : `${formatCents(deal.price_cents)} one-time`}
                </span>
              </Row>
              <Divider />
              <Row label="Expires">Never</Row>
            </dl>
            <small className="block text-ink-muted">
              {deal.free ? "No payment required." : "14-day money-back guarantee."}
            </small>
          </div>
        }
      />
    </>
  )
}

function FreeClaimStep({ email, slug }: { email: string; slug: string }) {
  const [claiming, setClaiming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function claim(e: FormEvent) {
    e.preventDefault()
    setClaiming(true)
    setError(null)
    const result = await fetchJson<{ free?: boolean; errors?: Record<string, string> }>(
      "/checkout/lifetime",
      { body: { slug } },
    )
    if (result.ok && result.data.free) {
      router.visit("/checkout/complete")
      return
    }
    setError(result.data.errors?.base ?? "Something went wrong. Please try again.")
    setClaiming(false)
  }

  return (
    <form onSubmit={claim} className="space-y-4">
      <p className="text-sm text-ink-muted">
        Redeeming as <strong className="text-ink-body">{email}</strong>
      </p>
      {error && <p className="text-sm text-danger-display">{error}</p>}
      <Button type="submit" disabled={claiming}>
        {claiming ? "One moment…" : "Claim your license"}
      </Button>
    </form>
  )
}

function LifetimePaymentStep({
  email,
  slug,
  priceCents,
}: {
  email: string
  slug: string
  priceCents: number
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A failed payment retries against the same PaymentIntent instead of
  // minting a new one on every click.
  const clientSecretRef = useRef<string | null>(null)

  async function pay(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError(null)

    const { error: submitError } = await elements.submit()
    if (submitError) {
      setError(submitError.message ?? "Please complete your payment details.")
      setProcessing(false)
      return
    }

    let clientSecret = clientSecretRef.current
    if (!clientSecret) {
      const result = await fetchJson<{ client_secret?: string; errors?: Record<string, string> }>(
        "/checkout/lifetime",
        { body: { slug } },
      )
      if (!result.ok || !result.data.client_secret) {
        setError(result.data.errors?.base ?? "Something went wrong. Please try again.")
        setProcessing(false)
        return
      }
      clientSecret = result.data.client_secret
      clientSecretRef.current = clientSecret
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: { return_url: `${window.location.origin}/checkout/complete` },
    })
    // Only reached on failure — success redirects.
    setError(confirmError?.message ?? "Payment failed. Please try again.")
    setProcessing(false)
  }

  return (
    <form onSubmit={pay} className="space-y-5">
      <p className="text-sm text-ink-muted">
        Purchasing as <strong className="text-ink-body">{email}</strong>
      </p>
      <PaymentElement
        options={{
          layout: "tabs",
          wallets: { applePay: "never", googlePay: "never", link: "never" },
        }}
      />
      {error && <p className="text-sm text-danger-display">{error}</p>}
      <Button type="submit" disabled={!stripe || processing} className="w-full sm:w-auto">
        {processing ? "Processing…" : `Pay ${formatCents(priceCents)}`}
      </Button>
      <p className="text-xs text-ink-muted">
        Secured by Stripe. 14-day money-back guarantee — email support@flywrite.app.
      </p>
    </form>
  )
}
